/**
 * Frontend route access aligned with App.js and BACKEND_STRUCTURE.md §1.
 * - public: no login required
 * - authenticated: ProtectedRoute (any logged-in user)
 * - presenter: EdVise internal team (founders + members; nav "More" menu); not school 主任
 * - staffOrPresenter: school 主任 (Sta) or EdVise internal team (e.g. /test-results)
 */

export const ROUTE_ACCESS = {
  '/': { level: 'public' },
  '/login': { level: 'public' },
  '/register': { level: 'public' },
  '/tutorials': { level: 'public' },
  '/jupas-calculator': { level: 'public' },
  '/jupas-survey': { level: 'public' },
  '/jupas-survey/submit': { level: 'public' },
  '/jupas-survey/success': { level: 'public' },
  '/jupasurvey': { level: 'public' },
  '/press-releases': { level: 'public' },
  '/hiring': { level: 'public' },
  '/team': { level: 'public' },
  '/privacy': { level: 'public' },
  '/view-report': { level: 'authenticated' },
  '/edit-profile': { level: 'public' },
  '/ability-test': { level: 'authenticated' },
  '/interest-test': { level: 'authenticated' },
  '/access': { level: 'public' },
  '/beta/feature-invites': { level: 'presenter' },
  '/bug-reports': { level: 'presenter' },
  '/jupasurvey-results': { level: 'presenter' },
  '/eddy': { level: 'presenter' },
  '/trajectory': { level: 'presenter' },
  '/presentation': { level: 'presenter' },
  '/analytics': { level: 'presenter' },
  '/test-results': { level: 'staffOrPresenter' },
  '/school': { level: 'schoolStaffOrPresenter' },
  '/beta/jupaselect': { level: 'presenter' },
  '/beta/schools': { level: 'presenter' },
  '/beta/promo-home': { level: 'presenter' },
  '/beta/eddy': { level: 'presenter' },
  '/join': { level: 'public' }
};

/** @typedef {'public' | 'authenticated' | 'presenter' | 'staffOrPresenter' | 'schoolStaffOrPresenter' | 'featureOrPresenter'} AccessLevel */

/**
 * @param {AccessLevel} level
 * @param {{ isAuthenticated: boolean, isPresenter: boolean, hasFeature?: (feature: string) => boolean }} ctx
 */
export function satisfiesAccessLevel(level, { isAuthenticated, isPresenter, isStaff, isSchoolStaff, hasFeature }) {
  if (level === 'public') return true;
  if (level === 'authenticated') return isAuthenticated;
  if (level === 'presenter') return isAuthenticated && isPresenter;
  if (level === 'staffOrPresenter') return isAuthenticated && (isPresenter || isStaff);
  if (level === 'schoolStaffOrPresenter') return isAuthenticated && (isPresenter || isSchoolStaff);
  return false;
}

/**
 * @param {{ level: 'featureOrPresenter', feature: string }} rule
 * @param {{ isAuthenticated: boolean, isPresenter: boolean, hasFeature?: (feature: string) => boolean }} ctx
 */
export function satisfiesFeatureOrPresenter(rule, ctx) {
  if (!ctx.isAuthenticated) return false;
  if (ctx.isPresenter) return true;
  return typeof ctx.hasFeature === 'function' && ctx.hasFeature(rule.feature);
}

/**
 * @param {string} pathname
 * @param {{ isAuthenticated: boolean, isPresenter: boolean }} ctx
 */
export function canAccessRoute(pathname, ctx) {
  const base = pathname.split('?')[0];
  if (base.startsWith('/access/')) return true;
  const rule = ROUTE_ACCESS[base];
  if (!rule) return false;
  if (rule.level === 'featureOrPresenter') {
    return satisfiesFeatureOrPresenter(rule, ctx);
  }
  return satisfiesAccessLevel(rule.level, ctx);
}

/**
 * @param {{ access: AccessLevel, route: string }} session
 * @param {{ isAuthenticated: boolean, isPresenter: boolean }} ctx
 */
export function canAccessTutorialSession(session, ctx) {
  return satisfiesAccessLevel(session.access, ctx);
}

/**
 * @param {Array<{ access: AccessLevel }>} sessions
 * @param {{ isAuthenticated: boolean, isPresenter: boolean }} ctx
 */
export function filterTutorialSessions(sessions, ctx) {
  return sessions.filter((s) => canAccessTutorialSession(s, ctx));
}

export function getTutorialStartPath(session) {
  return session.startPath || session.route;
}
